// Aviso de PLAN VENCIDO (modo consulta) y de PLAN POR VENCER (fase C, 18-ago-2026).
// Espejo del PlanVencidoBanner de la agenda dental, con los tokens de este front.
// Los datos vienen de /me/permissions (planReadOnly, planDueSoon) vía AuthContext —
// el umbral del "por vencer" lo decide el backend con la misma config que el correo
// del cron (5 días mensual / 7 anual); una cortesía no recibe correos, así que este
// banner es su único aviso antes de caer en modo consulta.
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CalendarClock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const fechaCorta = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' });

export default function PlanBanner() {
  const navigate = useNavigate();
  const { user, planReadOnly, planDueSoon } = useAuth();
  if (!planReadOnly && !planDueSoon) return null;
  if (user?.role !== 'admin') return null;   // profesores/alumnos: no es su asunto

  // Solo el dueño puede renovar (es quien tiene 'billing'): al resto se le informa,
  // sin mandarlo a una pantalla que no puede abrir.
  const esDueno = user?.baseRole === 'owner';
  const boton = esDueno && (
    <button
      type="button"
      onClick={() => navigate('/mi-plan')}
      className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-700"
    >
      Ver mi plan
    </button>
  );

  if (planReadOnly) {
    return (
      <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-500/40 dark:bg-amber-500/10">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Plan vencido — estás en modo consulta
            </p>
            <p className="mt-0.5 text-sm text-amber-800 dark:text-amber-300/90">
              Puedes ver toda la información y nada se ha perdido. Para volver a registrar
              alumnas, asistencias y pagos hay que renovar el plan.
            </p>
          </div>
          {boton}
        </div>
      </div>
    );
  }

  const n = planDueSoon!.days_left;
  const que = planDueSoon!.is_trial ? 'Tu periodo de cortesía' : 'Tu plan';
  const cuando = n <= 1 ? 'vence mañana' : `vence en ${n} días`;
  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 dark:border-amber-500/30 dark:bg-amber-500/5">
      <div className="flex items-start gap-3">
        <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            {que} {cuando} ({fechaCorta(planDueSoon!.expires_at)})
          </p>
          <p className="mt-0.5 text-sm text-amber-800 dark:text-amber-300/90">
            Para que el estudio siga funcionando sin interrupciones, renueva antes de esa fecha.
          </p>
        </div>
        {boton}
      </div>
    </div>
  );
}
